import { useQuery } from '@tanstack/react-query';

import type { BulkBiddingVehicleListVehicle } from 'Pages/api/bulk-bidding/vehicles/request';
import { getVehiclesForBulkBidding } from 'Services/api/bulkBidding';

import { BULK_BIDDING_CSV_FIELDS } from './bulkBiddingCSV.const';
import type { InvalidCSVEntry, ValidatedCSVEntries } from './validateCSVEntries.types';

export interface ActionableVehicle {
	bidAmount: number;
	vehicle: BulkBiddingVehicleListVehicle;
}

export interface UnactionableVehicle {
	bidAmount?: string;
	errors?: InvalidCSVEntry['errors'];
	vehicle: BulkBiddingVehicleListVehicle;
}

export interface GetVehiclesForBulkBidding {
	actionable: ActionableVehicle[];
	unactionable: UnactionableVehicle[];
}

const fetchVehiclesForBulkBidding = async ({
	invalid,
	valid,
}: ValidatedCSVEntries): Promise<GetVehiclesForBulkBidding> => {
	const allIds = [...valid.map((v) => v.enquiryId), ...invalid.map((v) => v.enquiryId)];

	const validBidsByEnquiryId: [number, ActionableVehicle['bidAmount']][] = valid.map(({ bidAmount, enquiryId }) => [
		Number(enquiryId),
		bidAmount,
	]);
	const validEntries = new Map(validBidsByEnquiryId);

	const errorsAndCSVBidAmountByEnquiryId: [
		number,
		{ bidAmount: UnactionableVehicle['bidAmount']; errors: UnactionableVehicle['errors'] },
	][] = invalid.map(({ csvData, enquiryId, errors }) => [
		Number(enquiryId),
		{ bidAmount: csvData[BULK_BIDDING_CSV_FIELDS.BID_AMOUNT], errors },
	]);
	const invalidEntriesMap = new Map(errorsAndCSVBidAmountByEnquiryId);

	const vehicles = await getVehiclesForBulkBidding({ enquiryIds: allIds });

	return vehicles.reduce<GetVehiclesForBulkBidding>(
		(acc, vehicle) => {
			const enquiryId = Number(vehicle.id);
			const bidAmount = validEntries.get(enquiryId);

			if (bidAmount) {
				acc.actionable.push({ bidAmount, vehicle });
				return acc;
			}
			const invalidEntry = invalidEntriesMap.get(enquiryId);
			acc.unactionable.push({ ...invalidEntry, vehicle });

			return acc;
		},
		{ actionable: [], unactionable: [] },
	);
};

export const useFetchVehiclesForBulkBidding = (validatedCSVEntries: Nullable<ValidatedCSVEntries>) => {
	return useQuery({
		enabled: Boolean(validatedCSVEntries),
		queryFn: () =>
			// Not on v5.25 so can't use skipToken
			validatedCSVEntries
				? fetchVehiclesForBulkBidding(validatedCSVEntries)
				: Promise.reject(new Error('No entries to fetch')),
		queryKey: ['validatedCSVEntries', validatedCSVEntries],
		refetchOnWindowFocus: false,
		retry: false,
	});
};
